import React, { FC, memo, useState } from 'react';
import { useIntl } from 'umi';
import { Bubble, Icon } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';

import BasicImage from '@home/components/BasicImage';
import closeIcon from '@/pages/aigc/assets/svg/skill-close.svg';
import closeActiveIcon from '@/pages/aigc/assets/svg/agent-skill-del-active.svg';
import defaultGameIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import chartTagArrowIcon from '@/pages/aigc/assets/svg/chartTagArrow.svg';
import ChartDashboardIcon from '@/pages/aigc/assets/svg/chart-dashboard.svg';
import './index.scss';

// ==================== 类型 ====================

export interface ISelectedChartTagData {
  /** 唯一标识（chart_id 或 dashboard u_id） */
  chartId: string;
  /** 复合唯一 key（gameCode:dashboardValue:chartId），用于跨报表/游戏去重 */
  uniqueKey: string;
  /** 英文展示名称 */
  nameEN: string;
  /** 中文展示名称 */
  nameCN: string;
  /** 所属报表中文名（charts 接口 dashboard_title，仅 chart_name 打点用） */
  dashboardNameCN?: string;
  /** 游戏 icon（game.cover） */
  gameIcon: string;
  /** 图表类型 icon（历史链路：由选中项直接携带） */
  chartTypeIcon: string;


  /** 游戏展示名（用于 hover tip 第一行展示） */
  gameName?: string;
  /** 选中类型：'dashboard'（整个报表）| 'chart'（单个图表） */
  type: 'dashboard' | 'chart';

  /** 所属游戏 code */
  gameCode: string;
  /** option.value（拼接 id），用于面板交互（tag 回点定位、region key） */
  dashboardValue?: string;
  /** 真实 dashboard_custom_id，用于后端 chart_ids 拼接；旧数据可 fallback 到 dashboardValue */
  realDashboardId?: string;
  /** dashboard 类型下的全部图表 id（提交时展开为 chart_ids） */
  dashboardChartIds?: string[];
  /** dashboard 全选时子图表中文名（与 dashboardChartIds 同序，展开 chart_name 打点） */
  dashboardChartNames?: string[];
  /** 真实图表类型（后端 chart_type，如 Table/Line） */
  chartRealType?: string;
  /** 是否处于 loading 占位态（外部入口数据未返回时） */
  isLoading?: boolean;
}

/**
 * 按当前界面语言解析标签展示文案
 */
export function resolveChartDisplayName(
  chart: ISelectedChartTagData,
  isEN: boolean,
): string {
  return isEN
    ? (chart.nameEN?.trim() || chart.nameCN?.trim() || '')
    : (chart.nameCN?.trim() || chart.nameEN?.trim() || '');
}

interface ISelectedChartTagProps {
  /** 选中的图表/报表数据 */
  chart: ISelectedChartTagData;
  /** 关闭（移除）回调，参数为 uniqueKey */
  onClose: (uniqueKey: string) => void;
  /** 点击 Tag 回调（激活浮窗 + 定位） */
  onClick?: (chart: ISelectedChartTagData) => void;
}

// ==================== 组件实现 ====================

/** BEM 前缀 */
const PREFIX = 'dba-selected-chart-tag';

export interface IChartTagTooltipChartItem {
  chartId?: string;
  typeIcon?: string;
  label: string;
}

export interface IChartTagTooltipGameItem {
  gameCode?: string;
  gameName: string;
  gameIcon?: string;
  charts: IChartTagTooltipChartItem[];
}

export interface IChartTagTooltipContentProps {
  title?: string;
  list: IChartTagTooltipGameItem[];
}

export const ChartTagTooltipContent: FC<IChartTagTooltipContentProps> = ({
  title,
  list,
}) => (
  <div className={`${PREFIX}__tip`}>
    {title && <div className={`${PREFIX}__tip-title`}>{title}</div>}
    <div className={`${PREFIX}__tip-list`}>
      {list.map((game, gameIndex) => (
        <div
          className={`${PREFIX}__tip-game-block`}
          key={`${game?.gameCode || game?.gameName || 'game'}-${gameIndex}`}
        >
          <div className={`${PREFIX}__tip-game`}>
            <BasicImage
              src={game?.gameIcon || defaultGameIcon}
              alt={game?.gameName || game?.gameCode || '-'}
              errorSrc={defaultGameIcon}
              className={`${PREFIX}__tip-game-icon`}
            />
            <span className={`${PREFIX}__tip-game-name`}>{game?.gameName || game?.gameCode || '-'}</span>
          </div>
          <div className={`${PREFIX}__tip-chart-list`}>
            {game?.charts?.map((chart, chartIndex) => (
              <div
                className={`${PREFIX}__tip-structure`}
                key={`${chart?.chartId || chart?.label || 'chart'}-${chartIndex}`}
              >
                <BasicImage
                  src={chart?.typeIcon || ChartDashboardIcon}
                  alt="chart-type"
                  errorSrc={ChartDashboardIcon}
                  className={`${PREFIX}__tip-structure-icon`}
                />
                <span className={`${PREFIX}__tip-structure-text`}>{chart?.label}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * 选中图表/报表标签组件

 * 设计稿样式：游戏icon + 图表icon + 名称 + 下拉箭头
 * hover 时第一个 icon（游戏icon）变 x 关闭按钮，点击可删除
 * 点击图表 icon / 名称 / 箭头区域可激活 ChartPopover 浮窗
 */
const SelectedChartTag: FC<ISelectedChartTagProps> = ({ chart, onClose, onClick }) => {
  const { locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const label = resolveChartDisplayName(chart, isEN);
  const gameName = chart.gameName?.trim() || chart.gameCode || '-';
  const [isCloseHovered, setIsCloseHovered] = useState<boolean>(false);


  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClose(chart.uniqueKey);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(chart);
  };

  /**
   * 阻止鼠标点击在标签内部插入光标
   * 防止光标进入该元素的可编辑区域
   */
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  /**
   * 图表类型 icon
   * - dashboard（整个报表）：使用报表专用 icon（ChartDashboardIcon）
   * - chart（具体图表）：使用具体图表类型 icon（来自 CHART_TYPE_ICON_MAP），无匹配时 fallback 到报表 icon
   */
  const typeIcon = (() => {
    if (chart.type === 'dashboard') return ChartDashboardIcon;
    const icon = chart.chartTypeIcon;
    return (typeof icon === 'string' && icon) ? icon : ChartDashboardIcon;
  })();

  const tipContent = (
    <ChartTagTooltipContent
      list={[
        {
          gameCode: chart.gameCode,
          gameName,
          gameIcon: chart.gameIcon,
          charts: [
            {
              chartId: chart.chartId,
              typeIcon,
              label,
            },
          ],
        },
      ]}
    />
  );


  return (
    <Bubble
      dark
      placement="top"
      arrowPointAtCenter
      content={tipContent}
      overlayClassName="dba-nav-menu-overlay dba-selected-chart-tag-tip"
    >
      <div

        className={PREFIX}
        contentEditable={false}
        suppressContentEditableWarning
        onMouseDown={handleMouseDown}
      >
        {/* 游戏 icon —— loading 占位时转圈；hover 时隐藏，被 close 按钮替代 */}
        {chart.isLoading ? (
          <span className={`${PREFIX}__game-icon ${PREFIX}__game-icon--loading`}>
            <Icon type="loading" />
          </span>
        ) : (
          <BasicImage
            src={chart.gameIcon || defaultGameIcon}
            alt={label}
            errorSrc={defaultGameIcon}
            className={`${PREFIX}__game-icon`}
          />
        )}
        {/* 关闭按钮 —— 默认隐藏，hover 时显示（替代游戏 icon） */}
        <div
          className={`${PREFIX}__close`}
          onClick={handleClose}
          onMouseEnter={() => setIsCloseHovered(true)}
          onMouseLeave={() => setIsCloseHovered(false)}
        >
          <BasicImage
            src={isCloseHovered ? closeActiveIcon : closeIcon}
            alt="close"
            errorSrc={closeIcon}
            className={`${PREFIX}__close-icon`}
          />
        </div>
        {/* 下拉热区：图表 icon + 名称 + 箭头（除左侧删除外均可展开浮窗） */}
        <div className={`${PREFIX}__dropdown-hit`} onClick={handleClick}>
          {/* TODO: 临时注释图表类型 icon，观察影响
          <BasicImage
            src={typeIcon}
            alt="chart-type"
            errorSrc={ChartDashboardIcon}
            className={`${PREFIX}__type-icon`}
          />
          */}
          <span className={`${PREFIX}__name`}>{label}</span>
          <span className={`${PREFIX}__arrow`}>
            <BasicImage
              src={chartTagArrowIcon}
              alt="arrow"
              errorSrc={chartTagArrowIcon}
              className={`${PREFIX}__arrow-img`}
            />
          </span>
        </div>
      </div>
    </Bubble>
  );

};

export default memo(SelectedChartTag);
